import { useIdsSelect } from '@entities/common/IdsSelector/index.ts';
import { useIdsSelecting } from '@entities/common/IdsSelector/models.ts';
import { useSaveData } from '@entities/common/savedData/index.ts';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';
import { useAppDispatch } from '@shared/hooks';
import type { ExerciseVideo } from '@shared/types/exercises';
import { useAppSelector } from '@src/library/hooks/common';

const exerciseAdapter = createEntityAdapter<ExerciseVideo>({});

const exerciseSlice = createSlice({
  name: 'exercises',
  initialState: exerciseAdapter.getInitialState(),
  reducers: {
    exerciseAdded: exerciseAdapter.addOne,
    exercisesAdd: exerciseAdapter.addMany,
    exercisesRemove: exerciseAdapter.removeAll,
    exerciseRemove: exerciseAdapter.removeOne,
    exercisesSet: exerciseAdapter.setAll,
    exerciseSet: exerciseAdapter.setOne,
    // exercisesReceived(state, action) {
    //   exerciseAdapter.setAll(state, action.payload.exercises);
    // },
  },
});

export const exercisesReducer = exerciseSlice.reducer;
export const exercisesActions = exerciseSlice.actions;

export const exercisesSelectors = exerciseAdapter.getSelectors();

export const useExercise = (id?: number) => {
  const dispatch = useAppDispatch();
  const exercise = useAppSelector((state) => {
    let exercise;
    id && (exercise = exercisesSelectors.selectById(state.exercisesReducer, id));
    return exercise;
  });

  const addExercise = (exercise: ExerciseVideo) =>
    dispatch(exercisesActions.exerciseAdded(exercise));

  const removeExercise = (id: number) => dispatch(exercisesActions.exerciseRemove(id));

  const setExercise = (exercise: ExerciseVideo) => dispatch(exercisesActions.exerciseSet(exercise));
  return {
    exercise,
    addExercise,
    setExercise,
    removeExercise,
  };
};

export const useExercises = () => {
  const dispatch = useAppDispatch();
  const addExercises = (exercises: ExerciseVideo[]) =>
    dispatch(exercisesActions.exercisesAdd(exercises));

  return {
    addExercises,
  };
};

export const useExerciseIdsSelect = () => {
  const { isIdSelected, doToggleId, selectedIds, doResetSelectedIds, doAddId, doRemoveId } =
    useIdsSelect('useExerciseIdsSelect');
  const { isSelecting, onBackOrReady, setIsSelectingFalse, setIsSelectingTrue} =
    useIdsSelecting('useExerciseIdsSelect');
  return {
    selectedIds: selectedIds || [],
    doToggleId,
    doAddId,
    doRemoveId,
    isIdSelected,
    doResetSelectedIds,
    isSelecting,
    onBackOrReady,
    setIsSelectingFalse,
    setIsSelectingTrue,
  };
};

export const useTempExercises = () => {
  const {
    data: tempExercisesData,
    doSetStorageData,
    doRemoveStorageData,
  } = useSaveData<ExerciseVideo[]>('tempExercises');
  const { addExercise: addExerciseToRTK, removeExercise: removeExerciseFromRTK } = useExercise();
  const { doRemoveId, doAddId } = useExerciseIdsSelect();

  const tempExerciseIds = useMemo(
    () => tempExercisesData?.map((el) => el.id) || [],
    [tempExercisesData]
  );

  const addTempExercise = useCallback(
    (data: ExerciseVideo, shouldAddToAll = true) => {
      const exercise = tempExercisesData?.find((el) => el.id === data.id);
      exercise
        ? doSetStorageData([...tempExercisesData.filter((el) => el.id !== data.id), { ...data }])
        : doSetStorageData(tempExercisesData ? [...tempExercisesData, { ...data }] : [{ ...data }]);
      shouldAddToAll && doAddId(data.id);
      addExerciseToRTK(data);
    },
    [tempExercisesData, doSetStorageData, doAddId, addExerciseToRTK]
  );

  const removeTempExercise = useCallback(
    (id: number) => {
      doSetStorageData([...tempExercisesData.filter((el) => el.id !== id)]);
      doRemoveId(id);
      removeExerciseFromRTK(id);
    },
    [tempExercisesData, doSetStorageData, doRemoveId, removeExerciseFromRTK]
  );

  const updateTempExercise = useCallback(
    (id: number, newData: ExerciseVideo) => {
      const exerciseUpdateIndex = tempExercisesData?.findIndex((el) => el.id === id);

      if (exerciseUpdateIndex) {
        const dataToSave = {
          ...tempExercisesData[exerciseUpdateIndex],
          ...newData,
        };
        tempExercisesData[exerciseUpdateIndex] = dataToSave;
        removeExerciseFromRTK(id);
        addExerciseToRTK(dataToSave);
        doSetStorageData([...tempExercisesData]);
      }
    },
    [tempExercisesData, doSetStorageData, removeExerciseFromRTK, addExerciseToRTK]
  );

  return {
    tempExerciseIds,
    tempExercisesData,
    addTempExercise,
    removeTempExercise,
    updateTempExercise,
    clearTempExercisesStorage: doRemoveStorageData,
  };
};
